import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import flashbookApi from '../services/flashbookApi';
import { queryKeys } from '../lib/queryClient';
import { useSoldStyle, useLabelSettings, useCropImage, useFileDrop } from '../hooks';
import { useTranslation } from '../i18n/useTranslation';
import { canvasToFile, deliverFiles, fetchUrlAsFile, isIOSFamilyDevice } from '../utils/fileDelivery';
import styles from './PlancheEditorPage.module.css';
import flashbookPolicy from '../constants/flashbook.json';

// 상수 및 유틸리티 import
// eslint-disable-next-line no-unused-vars
import { CANVAS, SAVE, INTERACTION } from '../constants/plancheConstants';
// eslint-disable-next-line no-unused-vars
import { screenOffsetToCanvas, calculateImageDisplay, calculateCenteredPosition } from '../utils/coordinateTransform';
// 썸네일 프록시 비활성화 - 안정성 문제로 원본 URL 직접 사용
// import { createThumbnailsBatch, clearAllThumbnails } from '../utils/imageProxy';

// 기준 캔버스 크기 (레거시 호환용 - 점진적 마이그레이션)
const BASE_CANVAS_WIDTH = CANVAS.BASE_WIDTH;
const BASE_CANVAS_HEIGHT = CANVAS.BASE_HEIGHT;

/**
 * PlancheEditorPage - 플렁슈 편집 페이지
 * 3x4 캔버스에 플래시 이미지 드래그/리사이즈 배치
 */
const PlancheEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const flashIdParam = searchParams.get('flashId');
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    // Refs
    const canvasRef = useRef(null);
    const scalerRef = useRef(null);
    const canvasWrapperRef = useRef(null);
    const singleViewRef = useRef(null);
    const fileInputRef = useRef(null);
    const replaceFileInputRef = useRef(null);
    const lastSavedHashRef = useRef(null);
    const saveStatusTimerRef = useRef(null);
    const workerRef = useRef(null);
    const processedImagesRef = useRef({});
    const processedImagesOrderRef = useRef([]); // LRU 순서 추적
    const croppedImagesRef = useRef({});
    const croppedImagesOrderRef = useRef([]); // croppedImages LRU 순서 추적
    const croppedUrlsRef = useRef({}); // 서버에 업로드된 크롭 URL 추적 (상태 비동기 문제 해결)
    const scrollPickerRef = useRef(null); // 스크롤 피커 ref
    const MAX_CACHED_IMAGES = 20; // LRU 캐시 최대 개수

    // ===== 커스텀 훅 =====
    // SOLD 스타일 (deactivation, 라인, 텍스트)
    const {
        deactivationStyle, setDeactivationStyle,
        lineWidth, setLineWidth,
        lineLength, setLineLength,
        lineColor, setLineColor,
        soldText, setSoldText,
        soldTextColor, setSoldTextColor,
        soldTextWeight, setSoldTextWeight,
        soldTextSize, setSoldTextSize,
        soldTextRotation, setSoldTextRotation,
        soldTextItalic, setSoldTextItalic,
    } = useSoldStyle();

    const [showLabel, setShowLabel] = useState(flashbookPolicy.settings.show_label);

    // 라벨 설정
    const {
        labelPosition, setLabelPosition,
        customLabelPos, setCustomLabelPos,
        isDraggingLabel, setIsDraggingLabel,
        labelFont, setLabelFont,
        labelWeight, setLabelWeight,
        labelSize, setLabelSize,
    } = useLabelSettings();

    // 크롭 관련
    const {
        showCropModal, setShowCropModal,
        cropFlashId, setCropFlashId,
        crop, setCrop,
        completedCrop, setCompletedCrop,
        croppedImages, setCroppedImages,
        cropImageRef,
        // eslint-disable-next-line no-unused-vars
        openCropModal,
        // eslint-disable-next-line no-unused-vars
        closeCropModal,
        removeCroppedImage,
    } = useCropImage();

    // 파일 드래그 앤 드롭 (나중에 processFlashFile과 연결)
    const [fileDropCallback, setFileDropCallback] = useState(null);
    const {
        isDragging: isDraggingFile,
        // eslint-disable-next-line no-unused-vars
        error: dropError,
        getDropZoneProps,
    } = useFileDrop({
        onFileDrop: (file) => fileDropCallback?.(file),
        acceptedTypes: ['image/png', 'image/jpeg'],
        maxSize: 10 * 1024 * 1024,
    });

    // ===== 기본 상태 =====
    const [planche, setPlanche] = useState(null);
    const [flashes, setFlashes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFlash, setSelectedFlash] = useState(null);

    // 드래그/리사이즈 관련
    const [dragType, setDragType] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [lastTouchDistance, setLastTouchDistance] = useState(null);
    const [lastTouchAngle, setLastTouchAngle] = useState(null);

    // 뷰/스와이프 관련
    const [viewIndex, setViewIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState('right');
    const [swipeStartX, setSwipeStartX] = useState(null);
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);

    // 배경 관련
    const [bgMode, setBgMode] = useState('none');
    const [customBgColor, setCustomBgColor] = useState('#ffffff');
    const [processedImages, setProcessedImages] = useState({});

    // 썸네일 프록시 비활성화 - 원본 URL 직접 사용
    // const [thumbnailImages, setThumbnailImages] = useState({});

    // 저장 관련
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null); // 커스텀 저장 메시지
    const [isReplacingFlash, setIsReplacingFlash] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // 저장 필요 표시
    const isCropUploadingRef = useRef(false); // 크롭 업로드 중 (ref로 관리)



    // Web Worker 초기화
    useEffect(() => {
        workerRef.current = new Worker('/workers/imageProcessor.worker.js');

        workerRef.current.onmessage = (e) => {
            const { flashId, arrayBuffer, success } = e.data;
            if (success && arrayBuffer) {
                // ArrayBuffer → Blob → Blob URL (메모리 관리 가능)
                const blob = new Blob([arrayBuffer], { type: 'image/png' });
                const blobUrl = URL.createObjectURL(blob);

                // 이전 Blob URL 해제
                if (processedImagesRef.current[flashId]?.startsWith('blob:')) {
                    URL.revokeObjectURL(processedImagesRef.current[flashId]);
                }
                processedImagesRef.current[flashId] = blobUrl;

                // LRU 순서 업데이트
                processedImagesOrderRef.current = processedImagesOrderRef.current.filter(id => id !== flashId);
                processedImagesOrderRef.current.push(flashId);

                // LRU 캐시 제한 적용 (가장 오래된 항목 제거)
                while (processedImagesOrderRef.current.length > MAX_CACHED_IMAGES) {
                    const oldestId = processedImagesOrderRef.current.shift();
                    if (processedImagesRef.current[oldestId]?.startsWith('blob:')) {
                        URL.revokeObjectURL(processedImagesRef.current[oldestId]);
                    }
                    delete processedImagesRef.current[oldestId];
                }

                // state도 ref와 동기화
                setProcessedImages({ ...processedImagesRef.current });
            }
        };

        workerRef.current.onerror = () => { };

        return () => {
            // 컴포넌트 언마운트 시 모든 Blob URL 해제
            Object.values(processedImagesRef.current).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            processedImagesRef.current = {};
            processedImagesOrderRef.current = [];

            // croppedImages Blob URL도 해제
            Object.values(croppedImagesRef.current).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            croppedImagesRef.current = {};
            croppedImagesOrderRef.current = [];

            if (workerRef.current) {
                workerRef.current.terminate();
            }
        };
    }, []);

    // 배경 제거된 이미지 생성 (Web Worker 사용 + 배치 처리)
    useEffect(() => {
        if (bgMode === 'none') {
            // 모든 Blob URL 해제
            Object.values(processedImagesRef.current).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            processedImagesRef.current = {};
            processedImagesOrderRef.current = []; // LRU 순서도 초기화
            setProcessedImages({});
            return;
        }

        if (!workerRef.current || flashes.length === 0) return;

        const targetColor = bgMode === 'white' ? '#ffffff' : customBgColor;
        const BATCH_SIZE = 5; // 한 번에 처리할 이미지 수
        const BATCH_DELAY = 100; // 배치 간 딜레이 (ms)

        // 배치 처리로 메모리 사용 분산
        const processBatch = (startIndex) => {
            const endIndex = Math.min(startIndex + BATCH_SIZE, flashes.length);

            for (let i = startIndex; i < endIndex; i++) {
                const flash = flashes[i];
                // 이미 처리된 이미지는 스킵
                if (processedImagesRef.current[flash.id]) continue;

                // 원본 URL 직접 사용 (썸네일 프록시 비활성화)
                const imageUrl = croppedImages[flash.id] || flash.image_url;
                workerRef.current.postMessage({
                    flashId: flash.id,
                    imageUrl,
                    targetColor,
                    tolerance: 30
                });
            }

            // 다음 배치 처리
            if (endIndex < flashes.length) {
                setTimeout(() => processBatch(endIndex), BATCH_DELAY);
            }
        };

        processBatch(0);
    }, [bgMode, customBgColor, flashes, croppedImages]);

    // Note: 스타일 및 라벨 설정은 useSoldStyle, useLabelSettings 훅에서 자동 처리됨

    // 캔버스 스케일 업데이트 (고정 캔버스를 스케일러 너비에 맞게 축소)
    useEffect(() => {
        const updateCanvasScale = () => {
            if (scalerRef.current && canvasRef.current) {
                const scalerRect = scalerRef.current.getBoundingClientRect();
                // scalerRect.width가 0이면 아직 레이아웃이 안 됨
                if (scalerRect.width > 0) {
                    // 너비 기준으로만 스케일 (높이는 aspect-ratio로 자동)
                    const scale = scalerRect.width / BASE_CANVAS_WIDTH;
                    canvasRef.current.style.transform = `scale(${scale})`;
                }
            }
        };

        // 여러 타이밍에 실행하여 레이아웃 확정 후 정확한 계산 보장
        updateCanvasScale(); // 즉시 시도
        setTimeout(updateCanvasScale, 50);  // 50ms 후
        setTimeout(updateCanvasScale, 150); // 150ms 후 (CSS 렌더링 완료 대기)
        setTimeout(updateCanvasScale, 300); // 300ms 후 (폰트/이미지 로드 대기)

        // ResizeObserver로 스케일러 크기 변화 감지
        const resizeObserver = new ResizeObserver(updateCanvasScale);
        if (scalerRef.current) {
            resizeObserver.observe(scalerRef.current);
        }

        // 윈도우 리사이즈 및 로드에도 대응
        window.addEventListener('resize', updateCanvasScale);
        window.addEventListener('load', updateCanvasScale);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateCanvasScale);
            window.removeEventListener('load', updateCanvasScale);
        };
    }, []);

    // 스타일 변경 (useSoldStyle 훅에서 localStorage 자동 저장)
    const handleStyleChange = (style) => {
        setDeactivationStyle(style);
    };

    // 크롭 적용 함수
    const applyCrop = useCallback(async () => {
        if (!completedCrop || !cropImageRef.current || !cropFlashId) return;

        isCropUploadingRef.current = true; // 크롭 업로드 시작

        const image = cropImageRef.current;
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // 원본 해상도 기준으로 캔버스 크기 설정 (화질 유지)
        let cropWidth = completedCrop.width * scaleX;
        let cropHeight = completedCrop.height * scaleY;

        // 최대 크기 제한 (2000px) - 413 에러 방지
        const MAX_SIZE = 2000;
        if (cropWidth > MAX_SIZE || cropHeight > MAX_SIZE) {
            const ratio = Math.min(MAX_SIZE / cropWidth, MAX_SIZE / cropHeight);
            cropWidth = Math.round(cropWidth * ratio);
            cropHeight = Math.round(cropHeight * ratio);
        }

        canvas.width = cropWidth;
        canvas.height = cropHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            cropWidth,
            cropHeight
        );

        // 서버에 업로드하여 URL 저장
        try {
            // 로컬 미리보기용 PNG blob
            const previewBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            // 서버 업로드용 JPEG blob (압축)
            const uploadBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));

            if (previewBlob && uploadBlob) {
                // 로컬 미리보기용 Blob URL (메모리 효율적)
                // 이전 Blob URL 해제 (세션 내 생성된 것만)
                if (croppedImagesRef.current[cropFlashId]?.startsWith('blob:')) {
                    URL.revokeObjectURL(croppedImagesRef.current[cropFlashId]);
                }
                const blobUrl = URL.createObjectURL(previewBlob);
                croppedImagesRef.current[cropFlashId] = blobUrl;

                // LRU 순서 업데이트
                croppedImagesOrderRef.current = croppedImagesOrderRef.current.filter(id => id !== cropFlashId);
                croppedImagesOrderRef.current.push(cropFlashId);

                // LRU 캐시 제한 적용 (가장 오래된 Blob URL만 제거)
                while (croppedImagesOrderRef.current.length > MAX_CACHED_IMAGES) {
                    const oldestId = croppedImagesOrderRef.current.shift();
                    if (croppedImagesRef.current[oldestId]?.startsWith('blob:')) {
                        URL.revokeObjectURL(croppedImagesRef.current[oldestId]);
                    }
                    delete croppedImagesRef.current[oldestId];
                }

                // 기존 state와 병합 (서버에서 복원된 크롭 유지)
                setCroppedImages(prev => ({ ...prev, [cropFlashId]: blobUrl }));

                // 크롭 후 processedImages 초기화 (배경 제거가 크롭된 이미지에 재적용되도록)
                if (processedImagesRef.current[cropFlashId]?.startsWith('blob:')) {
                    URL.revokeObjectURL(processedImagesRef.current[cropFlashId]);
                }
                delete processedImagesRef.current[cropFlashId];
                processedImagesOrderRef.current = processedImagesOrderRef.current.filter(id => id !== cropFlashId);
                setProcessedImages(prev => {
                    const newState = { ...prev };
                    delete newState[cropFlashId];
                    return newState;
                });

                // JPEG로 압축된 파일 업로드
                const file = new File([uploadBlob], `flash_${cropFlashId}_cropped.jpg`, { type: 'image/jpeg' });
                const uploadResult = await flashbookApi.uploadImage(file);
                // ref에 즉시 저장 (상태 비동기 문제 해결)
                croppedUrlsRef.current[cropFlashId] = uploadResult.image_url;
                // flashes 상태에 cropped_url 저장 (나중에 layout_data에 포함됨)
                setFlashes(prev => prev.map(f =>
                    f.id === cropFlashId ? { ...f, croppedUrl: uploadResult.image_url } : f
                ));
            }
        } catch (err) {
            console.error('Failed to upload cropped image:', err);
        } finally {
            isCropUploadingRef.current = false; // 크롭 업로드 완료
        }

        setShowCropModal(false);
        setCropFlashId(null);
        setCompletedCrop(null);
    }, [
        completedCrop,
        cropFlashId,
        cropImageRef,
        setCompletedCrop,
        setCropFlashId,
        setCroppedImages,
        setShowCropModal,
    ]);

    // 플렁슈 데이터 로드
    useEffect(() => {
        const fetchPlanche = async () => {
            try {
                const result = await flashbookApi.getPlanche(id);
                setPlanche(result.planche);

                // 레이아웃 데이터와 플래시 병합
                const rawLayoutData = result.planche?.layout_data;
                // JSON 문자열인 경우 파싱
                let layoutData;
                if (typeof rawLayoutData === 'string') {
                    try {
                        layoutData = JSON.parse(rawLayoutData);
                    } catch (e) {
                        console.error('[DEBUG] Failed to parse layout_data:', e);
                        layoutData = [];
                    }
                } else {
                    layoutData = Array.isArray(rawLayoutData) ? rawLayoutData : [];
                }
                const flashesWithLayout = (result.flashes || []).map(flash => {
                    const layout = layoutData.find(l => l.flash_id === flash.id);
                    return {
                        ...flash,
                        x: layout?.x ?? 50,
                        y: layout?.y ?? 50,
                        scale: layout?.scale ?? 1,
                        rotation: layout?.rotation ?? 0,
                        available: flash.available !== false,
                        // 플래시 이름 및 사이즈 (layout_data에서 복원)
                        name: layout?.name ?? flash.name ?? '',
                        size: layout?.size ?? flash.size ?? '',
                        // 개별 플래시 라벨 설정
                        showInfo: layout?.show_info ?? true,
                        infoPosition: layout?.info_position ?? flashbookPolicy.layout.info_position,
                        infoFont: layout?.info_font ?? 'sans-serif',
                        infoWeight: layout?.info_weight ?? '300',
                        infoSize: layout?.info_size ?? 14,
                        customInfoX: layout?.custom_info_x ?? 10,
                        customInfoY: layout?.custom_info_y ?? 10,
                        // 개별 뷰 설정
                        singleViewX: layout?.single_view_x ?? 0,
                        singleViewY: layout?.single_view_y ?? 0,
                        singleViewScale: layout?.single_view_scale ?? 1,
                        // 크롭 이미지 URL
                        croppedUrl: layout?.cropped_url ?? null
                    };
                });
                setFlashes(flashesWithLayout);

                // 크롭된 이미지 상태 복원
                const restoredCroppedImages = {};
                flashesWithLayout.forEach(f => {
                    if (f.croppedUrl) {
                        restoredCroppedImages[f.id] = f.croppedUrl;
                        // ref도 함께 업데이트 (서버 URL은 blob이 아니므로 해제 불필요)
                        croppedImagesRef.current[f.id] = f.croppedUrl;
                        croppedUrlsRef.current[f.id] = f.croppedUrl; // 서버 URL ref 업데이트
                    }
                });
                if (Object.keys(restoredCroppedImages).length > 0) {
                    setCroppedImages(restoredCroppedImages);
                    // processedImages 초기화 (크롭된 이미지로 배경 제거 재적용되도록)
                    processedImagesRef.current = {};
                    processedImagesOrderRef.current = [];
                    setProcessedImages({});
                }

                // 플렁슈 전체 설정 복원
                const settings = result.planche?.planche_settings;
                setShowLabel(settings?.show_label ?? flashbookPolicy.settings.show_label);
                if (settings) {
                    if (settings.deactivation_style) setDeactivationStyle(settings.deactivation_style);
                    if (settings.line_width) setLineWidth(settings.line_width);
                    if (settings.line_length) setLineLength(settings.line_length);
                    if (settings.line_color) setLineColor(settings.line_color);
                    // TEXT 스타일 설정 로드
                    if (settings.sold_text) setSoldText(settings.sold_text);
                    if (settings.sold_text_color) setSoldTextColor(settings.sold_text_color);
                    if (settings.sold_text_weight) setSoldTextWeight(settings.sold_text_weight);
                    if (settings.sold_text_size) setSoldTextSize(settings.sold_text_size);
                    if (settings.sold_text_rotation !== undefined) setSoldTextRotation(settings.sold_text_rotation);
                    if (settings.sold_text_italic !== undefined) setSoldTextItalic(settings.sold_text_italic);
                    // 배경 및 라벨 설정
                    if (settings.bg_mode) setBgMode(settings.bg_mode);
                    if (settings.custom_bg_color) setCustomBgColor(settings.custom_bg_color);
                    if (settings.label_position) setLabelPosition(settings.label_position);
                    if (settings.custom_label_pos) setCustomLabelPos(settings.custom_label_pos);
                    if (settings.label_font) setLabelFont(settings.label_font);
                    if (settings.label_weight) setLabelWeight(settings.label_weight);
                    if (settings.label_size) setLabelSize(settings.label_size);
                }
            } catch (err) {
                console.error('Failed to fetch planche:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchPlanche();
    }, [
        id,
        setCompletedCrop,
        setCroppedImages,
        setCustomBgColor,
        setCustomLabelPos,
        setDeactivationStyle,
        setLabelFont,
        setLabelPosition,
        setLabelSize,
        setLabelWeight,
        setLineColor,
        setLineLength,
        setLineWidth,
        setProcessedImages,
        setShowCropModal,
        setSoldText,
        setSoldTextColor,
        setSoldTextItalic,
        setSoldTextRotation,
        setSoldTextSize,
        setSoldTextWeight,
    ]);

    // URL에서 flashId 파라미터가 있으면 해당 플래시 자동 선택 및 개별 뷰로 이동
    useEffect(() => {
        if (!flashIdParam || loading || flashes.length === 0) return;

        const flashIndex = flashes.findIndex(f => f.id === flashIdParam);
        if (flashIndex !== -1) {
            setSelectedFlash(flashes[flashIndex]);
            setViewIndex(flashIndex + 1); // 개별 뷰로 이동 (인덱스 0은 전체 뷰)
        }
    }, [flashIdParam, loading, flashes]);

    useEffect(() => {
        setIsDownloadMenuOpen(false);
    }, [viewIndex]);

    // 변경 감지 - 저장 필요 여부 체크
    useEffect(() => {
        if (loading || flashes.length === 0) return;

        const currentState = {
            flashes: flashes.map(f => ({
                id: f.id, x: f.x, y: f.y, scale: f.scale, rotation: f.rotation,
                available: f.available, name: f.name, size: f.size,
                showInfo: f.showInfo, infoPosition: f.infoPosition,
                croppedUrl: f.croppedUrl
            })),
            deactivationStyle, lineWidth, lineLength, lineColor,
            soldText, soldTextColor, soldTextWeight, soldTextSize, soldTextRotation, soldTextItalic,
            bgMode, customBgColor, showLabel, labelPosition, customLabelPos, labelFont, labelWeight, labelSize
        };
        const currentHash = JSON.stringify(currentState);

        // 초기 로드 후 첫 해시 저장
        if (!lastSavedHashRef.current) {
            lastSavedHashRef.current = currentHash;
            return;
        }

        setHasUnsavedChanges(currentHash !== lastSavedHashRef.current);
    }, [flashes, deactivationStyle, lineWidth, lineLength, lineColor,
        soldText, soldTextColor, soldTextWeight, soldTextSize, soldTextRotation, soldTextItalic,
        bgMode, customBgColor, showLabel, labelPosition, customLabelPos, labelFont, labelWeight, labelSize, loading]);

    // 썸네일 프록시 비활성화 - 안정성 문제로 원본 URL 직접 사용
    // useEffect(() => {
    //     if (flashes.length === 0) return;
    //     const generateThumbnails = async () => {
    //         const imagesToProcess = flashes
    //             .filter(f => f.image_url)
    //             .map(f => ({ id: f.id, url: f.image_url }));
    //         if (imagesToProcess.length === 0) return;
    //         const thumbnails = await createThumbnailsBatch(imagesToProcess);
    //         setThumbnailImages(prev => {
    //             const newState = { ...prev };
    //             thumbnails.forEach((url, id) => {
    //                 if (!newState[id]) {
    //                     newState[id] = url;
    //                 }
    //             });
    //             return newState;
    //         });
    //     };
    //     generateThumbnails();
    // }, [flashes]);
    //
    // useEffect(() => {
    //     return () => {
    //         clearAllThumbnails();
    //     };
    // }, []);

    // 파일 처리 로직 (업로드 및 플래시 생성)
    const processFlashFile = useCallback(async (file) => {
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            alert('파일 크기는 10MB 이하여야 합니다.');
            return;
        }

        try {
            const uploadResult = await flashbookApi.uploadImage(file);
            const flashResult = await flashbookApi.createFlash({
                planche_id: id,
                image_url: uploadResult.image_url,
                name: file.name.split('.')[0],
                size: '',
                available: true,
                label_style: { fontSize: 12, fontWeight: 'normal', x: 5, y: 5 }
            });

            const newFlash = {
                ...flashResult.flash,
                x: 50,
                y: 50,
                scale: 1,
                rotation: 0
            };

            setFlashes(prev => [...prev, newFlash]);
        } catch (err) {
            console.error('Failed to upload flash:', err);
            alert('이미지 업로드에 실패했습니다.');
        }
    }, [id]);

    // 파일 input 핸들러
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        await processFlashFile(file);
        // input 초기화 (같은 파일 다시 선택 가능)
        e.target.value = '';
    };

    // 드래그 앤 드롭 콜백 연결
    useEffect(() => {
        setFileDropCallback(() => processFlashFile);
    }, [processFlashFile]);

    // 드래그 시작
    const handleMouseDown = (e, flash) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedFlash(flash);
        setDragType('planche');

        if (!canvasRef.current || !scalerRef.current) return;
        const scalerRect = scalerRef.current.getBoundingClientRect();
        const scale = scalerRect.width / BASE_CANVAS_WIDTH;

        // 스케일을 고려한 오프셋 계산
        setDragOffset({
            x: (e.clientX - scalerRect.left) / scale - (flash.x || 0),
            y: (e.clientY - scalerRect.top) / scale - (flash.y || 0)
        });
    };

    // 드래그 중
    const handleMouseMove = useCallback((e) => {
        if (!selectedFlash || !scalerRef.current) return;

        const scalerRect = scalerRef.current.getBoundingClientRect();
        const scale = scalerRect.width / BASE_CANVAS_WIDTH;

        if (dragType === 'planche') {
            // 스케일을 고려한 픽셀 좌표 계산
            const x = (e.clientX - scalerRect.left) / scale - dragOffset.x;
            const y = (e.clientY - scalerRect.top) / scale - dragOffset.y;

            setFlashes(prev => prev.map(f =>
                f.id === selectedFlash.id ? { ...f, x, y } : f
            ));
        }

        // 라벨 드래그
        if (isDraggingLabel && labelPosition === 'custom') {
            const x = (e.clientX - scalerRect.left) / scale - dragOffset.x;
            const y = (e.clientY - scalerRect.top) / scale - dragOffset.y;
            setCustomLabelPos({ x, y });
        }
    }, [dragType, selectedFlash, dragOffset, isDraggingLabel, labelPosition, setCustomLabelPos]);

    // 드래그 종료
    const handleMouseUp = useCallback(() => {
        setDragType(null);
        setIsResizing(false);
        setIsDraggingLabel(false);
    }, [setIsDraggingLabel]);


    // 리사이즈 (휠) - 트랙패드/마우스 휠 정규화
    const handleWheel = useCallback((e, flash) => {
        e.preventDefault();
        // deltaY를 정규화하여 트랙패드와 마우스 휠 모두 부드럽게 동작
        // 트랙패드: 작은 값 (1~10), 마우스 휠: 큰 값 (100+)
        const normalizedDelta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY) * 0.002, 0.1);
        const rawScale = Math.max(0.1, Math.min(5, (flash.scale || 1) - normalizedDelta));
        const newScale = Math.round(rawScale * 10) / 10;  // 소수점 1자리로 정리

        setFlashes(prev => prev.map(f =>
            f.id === flash.id ? { ...f, scale: newScale } : f
        ));
    }, []);

    // ===== 터치 이벤트 =====

    // 터치 시작
    const handleTouchStart = (e, flash) => {
        e.preventDefault();
        setSelectedFlash(flash);

        if (e.touches.length === 1) {
            // 싱글 터치: 드래그
            setDragType('planche');
            const touch = e.touches[0];
            if (!scalerRef.current) return;
            const scalerRect = scalerRef.current.getBoundingClientRect();
            const scale = scalerRect.width / BASE_CANVAS_WIDTH;

            setDragOffset({
                x: (touch.clientX - scalerRect.left) / scale - (flash.x || 0),
                y: (touch.clientY - scalerRect.top) / scale - (flash.y || 0)
            });
        } else if (e.touches.length === 2) {
            // 두 손가락: 핀치 줌 + 회전 준비
            const distance = getTouchDistance(e.touches);
            setLastTouchDistance(distance);
            setLastTouchAngle(getTwoFingerAngle(e.touches));
        }
    };

    // 두 손가락 거리 계산
    const getTouchDistance = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // 두 손가락 각도 계산 (회전용)
    const getTwoFingerAngle = (touches) => {
        const dx = touches[1].clientX - touches[0].clientX;
        const dy = touches[1].clientY - touches[0].clientY;
        return Math.atan2(dy, dx) * (180 / Math.PI);
    };

    // 터치 이동
    const handleTouchMove = useCallback((e) => {
        if (!selectedFlash) return;
        e.preventDefault();

        if (e.touches.length === 1 && dragType === 'planche') {
            // 드래그 (픽셀 기반)
            const touch = e.touches[0];
            if (!scalerRef.current) return;
            const scalerRect = scalerRef.current.getBoundingClientRect();
            const scale = scalerRect.width / BASE_CANVAS_WIDTH;

            const x = (touch.clientX - scalerRect.left) / scale - dragOffset.x;
            const y = (touch.clientY - scalerRect.top) / scale - dragOffset.y;

            setFlashes(prev => prev.map(f =>
                f.id === selectedFlash.id ? { ...f, x, y } : f
            ));
        } else if (e.touches.length === 2) {
            // 두 손가락: 핀치 줌 + 회전
            const distance = getTouchDistance(e.touches);
            const angle = getTwoFingerAngle(e.touches);

            // 첫 두 손가락 터치일 때 초기화 (한 손가락에서 전환된 경우)
            if (lastTouchDistance === null) {
                setLastTouchDistance(distance);
                setLastTouchAngle(angle);
                setDragType(null); // 드래그 해제
                return;
            }

            // 스케일 & 회전 변경 (prev 상태에서 현재 값 읽기 - stale closure 방지)
            const scaleDelta = (distance - lastTouchDistance) * 0.01;
            const angleDelta = lastTouchAngle !== null ? angle - lastTouchAngle : 0;

            setFlashes(prev => prev.map(f => {
                if (f.id !== selectedFlash.id) return f;
                const currentScale = f.scale || 1;
                const currentRotation = f.rotation || 0;
                const rawScale = Math.max(0.1, currentScale + scaleDelta);
                return {
                    ...f,
                    scale: Math.round(rawScale * 10) / 10,  // 소수점 1자리로 정리
                    rotation: (currentRotation + angleDelta) % 360
                };
            }));
            setLastTouchDistance(distance);
            setLastTouchAngle(angle);
        }
    }, [dragType, selectedFlash, dragOffset, lastTouchDistance, lastTouchAngle]);

    // 터치 종료
    const handleTouchEnd = useCallback(() => {
        setDragType(null);
        setLastTouchDistance(null);
        setLastTouchAngle(null);
    }, []);

    // 캔버스에서 휠 시 페이지 스크롤 방지 (document 레벨)
    useEffect(() => {
        const handleWheel = (e) => {
            // 실제 캔버스 영역(scalerRef) 내부인지 확인
            if (scalerRef.current?.contains(e.target)) {
                // 수직 스크롤이 있을 때 방지
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                    e.preventDefault();
                }
            }
        };

        document.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            document.removeEventListener('wheel', handleWheel);
        };
    }, []);

    // 선택된 플래시 크기 조절 (모바일 버튼용)
    const handleScaleChange = (delta) => {
        if (!selectedFlash) return;
        setFlashes(prev => {
            const flash = prev.find(f => f.id === selectedFlash.id);
            if (!flash) return prev;
            const newScale = Math.max(0.1, Math.round(((flash.scale || 1) + delta) * 10) / 10);
            return prev.map(f => f.id === selectedFlash.id ? { ...f, scale: newScale } : f);
        });
        setSelectedFlash(prev => {
            if (!prev) return null;
            const newScale = Math.max(0.1, Math.round(((prev.scale || 1) + delta) * 10) / 10);
            return { ...prev, scale: newScale };
        });
    };

    // 선택된 플래시 회전 (모바일 버튼용)
    const handleRotate = (delta = 5) => {
        if (!selectedFlash) return;
        setFlashes(prev => {
            const flash = prev.find(f => f.id === selectedFlash.id);
            if (!flash) return prev;
            const newRotation = ((flash.rotation || 0) + delta + 360) % 360;
            return prev.map(f => f.id === selectedFlash.id ? { ...f, rotation: newRotation } : f);
        });
        setSelectedFlash(prev => {
            if (!prev) return null;
            const newRotation = ((prev.rotation || 0) + delta + 360) % 360;
            return { ...prev, rotation: newRotation };
        });
    };

    // Hold-to-repeat 기능
    const holdIntervalRef = useRef(null);
    const startHoldAction = (action) => {
        action(); // 즉시 1회 실행
        holdIntervalRef.current = setInterval(action, 100); // 100ms마다 반복
    };
    const stopHoldAction = () => {
        if (holdIntervalRef.current) {
            clearInterval(holdIntervalRef.current);
            holdIntervalRef.current = null;
        }
    };

    // 마우스/터치 이벤트 리스너
    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // 캔버스 렌더링 공통 함수
    const renderCanvas = async (scale = 1) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const canvasElement = canvasRef.current;

        if (!canvasElement) return null;

        // 고정된 3:4 비율 캔버스 (화면 크기와 무관)
        const baseWidth = BASE_CANVAS_WIDTH;
        const baseHeight = BASE_CANVAS_HEIGHT;

        canvas.width = baseWidth * scale;
        canvas.height = baseHeight * scale;
        ctx.scale(scale, scale);

        // 흰색 배경
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, baseWidth, baseHeight);

        // 플렁슈 라벨 위치 계산
        let labelX = 10, labelY = 20;
        if (labelPosition === 'tl') { labelX = 10; labelY = parseInt(labelSize) + 5; }
        else if (labelPosition === 'tr') { labelX = baseWidth - 100; labelY = parseInt(labelSize) + 5; }
        else if (labelPosition === 'bl') { labelX = 10; labelY = baseHeight - 10; }
        else if (labelPosition === 'br') { labelX = baseWidth - 100; labelY = baseHeight - 10; }
        else if (labelPosition === 'custom') { labelX = customLabelPos.x; labelY = customLabelPos.y + parseInt(labelSize); }

        // 플렁슈 라벨
        ctx.fillStyle = '#333333';
        // Canvas font: [style] [weight] size family
        // labelWeight가 숫자(300, 400 등)이면 그대로 사용
        const fontWeight = labelWeight === 'normal' ? '400' : labelWeight === 'bold' ? '700' : labelWeight;
        ctx.font = `${fontWeight} ${labelSize}px ${labelFont}`;

        // CSS letter-spacing: 1px 시뮬레이션
        const labelText = `PLANCHE ${planche?.planche_number}`;
        const letterSpacing = 1; // CSS letter-spacing: 1px (scale 이미 font에 적용됨)
        let currentX = labelX;
        for (const char of (showLabel ? labelText : '')) {
            ctx.fillText(char, currentX, labelY);
            currentX += ctx.measureText(char).width + letterSpacing;
        }

        // 모든 플래시 이미지 병렬 로드
        const loadImage = (src) => new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });

        // 배경색 제거 함수
        const removeBackground = (img, targetColorHex, tolerance = 30) => {
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            tempCanvas.width = img.naturalWidth;
            tempCanvas.height = img.naturalHeight;
            tempCtx.drawImage(img, 0, 0);

            const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            const data = imageData.data;

            // hex to rgb
            const r = parseInt(targetColorHex.slice(1, 3), 16);
            const g = parseInt(targetColorHex.slice(3, 5), 16);
            const b = parseInt(targetColorHex.slice(5, 7), 16);

            for (let i = 0; i < data.length; i += 4) {
                const dr = Math.abs(data[i] - r);
                const dg = Math.abs(data[i + 1] - g);
                const db = Math.abs(data[i + 2] - b);
                if (dr < tolerance && dg < tolerance && db < tolerance) {
                    data[i + 3] = 0; // alpha = 0 (transparent)
                }
            }

            tempCtx.putImageData(imageData, 0, 0);
            return tempCanvas;
        };

        // 이미지 병렬 로드 (순차→동시 최적화)
        const imagePromises = flashes.map(async (flash) => {
            try {
                const imageSrc = processedImages[flash.id] || croppedImages[flash.id] || flash.image_url;
                if (!imageSrc) return null;

                let img = await loadImage(imageSrc);

                // 배경 제거 적용 - processedImages에 캐시가 있으면 스킵
                const hasProcessedCache = processedImages[flash.id];
                if (!hasProcessedCache && bgMode === 'white') {
                    img = removeBackground(img, '#ffffff', 30);
                } else if (!hasProcessedCache && bgMode === 'custom') {
                    img = removeBackground(img, customBgColor, 30);
                }
                return { flash, img };
            } catch (e) {
                console.warn('Image load failed for flash:', flash.id, e);
                return null;
            }
        });

        const loadedImages = (await Promise.all(imagePromises)).filter(Boolean);

        for (const { flash, img } of loadedImages) {

            // 좌표는 픽셀 단위로 저장되므로 그대로 사용
            const x = flash.x || 0;
            const y = flash.y || 0;
            const flashScale = flash.scale || 1;
            const rotation = flash.rotation || 0;

            // CSS와 동일하게 max-width: 120px, max-height: 120px 로직
            const maxSize = 120;
            const imgWidth = img.naturalWidth || img.width;
            const imgHeight = img.naturalHeight || img.height;

            // 원본 비율 유지하면서 max 120px 내에 맞춤
            let drawWidth, drawHeight;
            if (imgWidth > imgHeight) {
                if (imgWidth > maxSize) {
                    drawWidth = maxSize;
                    drawHeight = (imgHeight / imgWidth) * maxSize;
                } else {
                    drawWidth = imgWidth;
                    drawHeight = imgHeight;
                }
            } else {
                if (imgHeight > maxSize) {
                    drawHeight = maxSize;
                    drawWidth = (imgWidth / imgHeight) * maxSize;
                } else {
                    drawWidth = imgWidth;
                    drawHeight = imgHeight;
                }
            }

            ctx.save();
            // CSS transform-origin: center center와 일치하도록
            ctx.translate(x + drawWidth / 2, y + drawHeight / 2);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.scale(flashScale, flashScale);

            if (!flash.available && deactivationStyle === 'dim') {
                ctx.globalAlpha = 0.3;
            }

            ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
            ctx.restore();

            // SOLD 표시 (dim이 아닐 때) - 회전 영향 없이 정 각도로 표시
            if (!flash.available && deactivationStyle !== 'dim') {
                ctx.save();
                // 이미지 중심으로 이동 (회전 없이)
                ctx.translate(x + drawWidth / 2, y + drawHeight / 2);
                ctx.scale(flashScale, flashScale);

                ctx.strokeStyle = lineColor;
                ctx.lineWidth = lineWidth / flashScale;
                // CSS: width: `${lineLength}%` = 컨테이너(이미지) 너비의 퍼센트
                const lineLen = drawWidth * (lineLength / 100);

                // 첫 번째 대각선 (135도 = 좌상단→우하단)
                if (deactivationStyle === 'diagonal' || deactivationStyle === 'cross') {
                    ctx.beginPath();
                    ctx.moveTo(-lineLen / 2 * Math.cos(Math.PI / 4), -lineLen / 2 * Math.sin(Math.PI / 4));
                    ctx.lineTo(lineLen / 2 * Math.cos(Math.PI / 4), lineLen / 2 * Math.sin(Math.PI / 4));
                    ctx.stroke();
                }
                // 두 번째 대각선 (45도 = 우상단→좌하단)
                if (deactivationStyle === 'cross') {
                    ctx.beginPath();
                    ctx.moveTo(lineLen / 2 * Math.cos(Math.PI / 4), -lineLen / 2 * Math.sin(Math.PI / 4));
                    ctx.lineTo(-lineLen / 2 * Math.cos(Math.PI / 4), lineLen / 2 * Math.sin(Math.PI / 4));
                    ctx.stroke();
                }
                // 텍스트 스타일
                if (deactivationStyle === 'text') {
                    ctx.rotate(soldTextRotation * Math.PI / 180);
                    const fontSize = Math.min(drawWidth, drawHeight) * (soldTextSize / 100);
                    ctx.font = `${soldTextItalic ? 'italic ' : ''}${soldTextWeight} ${fontSize}px sans-serif`;
                    ctx.fillStyle = soldTextColor;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(soldText, 0, 0);
                }
                ctx.restore();
            }
            // 플렁슈 뷰에서는 개별 플래시 라벨 표시 안 함 (화면과 일치)
        }

        return canvas;
    };

    // 저장 (최적화: 병렬 처리 + 즉각 피드백 + 썸네일 캐싱)
    const handleSave = async () => {
        if (isSaving) return; // 중복 저장 방지

        // 크롭 업로드가 진행 중이면 완료될 때까지 대기
        if (isCropUploadingRef.current) {
            setSaveMessage('크롭 업로드 중...');
            // 최대 10초 대기 (100ms x 100회)
            for (let i = 0; i < 100; i++) {
                await new Promise(resolve => setTimeout(resolve, 100));
                if (!isCropUploadingRef.current) break; // 업로드 완료되면 중단
            }
            setSaveMessage(null);
        }

        setIsSaving(true);

        try {
            // 1단계: 데이터 준비 (동기)
            const layoutData = flashes.map(f => ({
                ...(Array.isArray(planche?.layout_data) ? planche.layout_data.find(entry => entry.flash_id === f.id) : {}),
                flash_id: f.id,
                x: f.x || 0,
                y: f.y || 0,
                scale: f.scale || 1,
                rotation: f.rotation || 0,
                available: f.available !== false,
                name: f.name || '',
                size: f.size || '',
                show_info: f.showInfo !== false,
                info_position: f.infoPosition || flashbookPolicy.layout.info_position,
                info_font: f.infoFont || 'sans-serif',
                info_weight: f.infoWeight || '300',
                info_size: f.infoSize || 14,
                custom_info_x: f.customInfoX || 10,
                custom_info_y: f.customInfoY || 10,
                single_view_x: f.singleViewX || 0,
                single_view_y: f.singleViewY || 0,
                single_view_scale: f.singleViewScale || 1,
                cropped_url: croppedUrlsRef.current[f.id] || f.croppedUrl || null
            }));


            const plancheSettings = {
                ...planche?.planche_settings,
                deactivation_style: deactivationStyle,
                line_width: lineWidth,
                line_length: lineLength,
                line_color: lineColor,
                // TEXT 스타일 옵션
                sold_text: soldText,
                sold_text_color: soldTextColor,
                sold_text_weight: soldTextWeight,
                sold_text_size: soldTextSize,
                sold_text_rotation: soldTextRotation,
                sold_text_italic: soldTextItalic,
                // 배경 및 라벨
                bg_mode: bgMode,
                custom_bg_color: customBgColor,
                show_label: showLabel,
                label_position: labelPosition,
                custom_label_pos: customLabelPos,
                label_font: labelFont,
                label_weight: labelWeight,
                label_size: labelSize
            };

            // 시각적 상태 해시 생성 (썸네일에 영향 주는 요소만)
            const visualState = {
                flashes: flashes.map(f => ({
                    id: f.id,
                    x: f.x,
                    y: f.y,
                    scale: f.scale,
                    rotation: f.rotation,
                    available: f.available,
                    croppedUrl: f.croppedUrl
                })),
                deactivationStyle,
                lineWidth,
                lineLength,
                lineColor,
                bgMode,
                customBgColor,
                showLabel,
                labelPosition,
                customLabelPos,
                labelFont,
                labelWeight,
                labelSize
            };
            const currentHash = JSON.stringify(visualState);
            const needsThumbnailUpdate = currentHash !== lastSavedHashRef.current;

            // 2단계: 먼저 데이터 저장 (빠른 응답)
            const savedPlanche = await flashbookApi.updatePlanche(id, {
                layout_data: layoutData,
                planche_settings: plancheSettings
            });

            // 저장 완료 피드백 (썬네일 완료 전)
            setIsSaving(false);
            lastSavedHashRef.current = currentHash; // 해시 업데이트
            setHasUnsavedChanges(false); // 저장 필요 표시 제거

            // Optimistic Update: 즉시 "저장됨" 표시
            if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
            setSaveStatus('saved');
            saveStatusTimerRef.current = setTimeout(() => setSaveStatus(null), 2000);

            // 3단계: 썬네일 생성 (완전 백그라운드, await 없음)
            if (needsThumbnailUpdate) {
                renderCanvas(1).then(async (thumbnailCanvas) => {
                    if (!thumbnailCanvas) return;
                    try {
                        const blob = await new Promise(resolve =>
                            thumbnailCanvas.toBlob(resolve, 'image/png')
                        );
                        if (!blob) return;

                        const file = new File([blob], `planche_${id}_thumbnail.png`, { type: 'image/png' });
                        const uploadResult = await flashbookApi.uploadImage(file);
                        if (uploadResult?.image_url) {
                            await flashbookApi.updatePlanche(id, { thumbnail_url: uploadResult.image_url, expected_version: savedPlanche.version });
                        }
                    } catch (e) {
                        console.warn('Thumbnail generation failed:', e);
                    }
                });
            }

        } catch (err) {
            console.error('Failed to save:', err);
            if (err.status === 409 || err.message?.includes('다른 화면')) alert(err.message);
            setIsSaving(false);

            // 에러 피드백
            if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
            setSaveStatus('error');
            saveStatusTimerRef.current = setTimeout(() => setSaveStatus(null), 3000);
        }
    };

    // 개별 플래시 이미지 교체
    const handleReplaceFlash = async (e) => {
        const file = e.target.files?.[0];
        if (!file || viewIndex === 0) return;

        const flash = flashes[viewIndex - 1];
        setIsReplacingFlash(true);

        try {
            // 새 이미지 업로드
            const uploadResult = await flashbookApi.uploadImage(file);

            // 플래시 이미지 URL 업데이트
            await flashbookApi.updateFlash(flash.id, {
                image_url: uploadResult.image_url
            });

            // 로컬 상태 업데이트 (croppedUrl도 초기화)
            setFlashes(prev => prev.map(f =>
                f.id === flash.id ? { ...f, image_url: uploadResult.image_url, croppedUrl: null } : f
            ));

            // 크롭/처리된 이미지 초기화 (refs도 함께 정리)
            // croppedImages ref 정리
            if (croppedImagesRef.current[flash.id]?.startsWith('blob:')) {
                URL.revokeObjectURL(croppedImagesRef.current[flash.id]);
            }
            delete croppedImagesRef.current[flash.id];
            croppedImagesOrderRef.current = croppedImagesOrderRef.current.filter(id => id !== flash.id);

            // processedImages ref 정리
            if (processedImagesRef.current[flash.id]?.startsWith('blob:')) {
                URL.revokeObjectURL(processedImagesRef.current[flash.id]);
            }
            delete processedImagesRef.current[flash.id];
            processedImagesOrderRef.current = processedImagesOrderRef.current.filter(id => id !== flash.id);

            // 훅의 내부 상태도 정리 (blobUrlsRef, accessOrderRef)
            removeCroppedImage(flash.id);

            // processedImages state 초기화
            setProcessedImages(prev => {
                const newState = { ...prev };
                delete newState[flash.id];
                return newState;
            });

            alert('이미지가 교체되었습니다.');
        } catch (err) {
            console.error('Failed to replace flash image:', err);
            alert('이미지 교체에 실패했습니다.');
        } finally {
            setIsReplacingFlash(false);
            if (replaceFileInputRef.current) {
                replaceFileInputRef.current.value = '';
            }
        }
    };

    // 플렁슈 삭제
    const handleDelete = async () => {
        const confirmDelete = window.confirm(
            `정말 PLANCHE ${planche?.planche_number}를(을) 삭제하시겠습니까?\n\n삭제하면 복구할 수 없으며, 관련된 모든 플래시 이미지와 데이터가 함께 삭제됩니다.`
        );

        if (!confirmDelete) return;

        try {
            await flashbookApi.deletePlanche(id);
            // 플렁슈 목록 캐시 무효화
            queryClient.invalidateQueries({
                queryKey: queryKeys.flashbook.planches.all()
            });
            navigate('/flashbook');
        } catch (error) {
            console.error('Failed to delete planche:', error);
            alert('플렁슈 삭제에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const buildFlashDownloadNameBase = (flash, variant) => {
        const identifier = String(flash?.flash_number || flash?.id || 'flash')
            .trim()
            .replace(/[^a-zA-Z0-9._-]+/g, '_');

        return `${identifier}_${variant}`;
    };

    // 이미지 다운로드 (고해상도)
    const handleDownload = async () => {
        try {
            // 플렁슈 캔버스 렌더링
            const plancheCanvas = await renderCanvas(CANVAS.RENDER_SCALE); // 4x 고해상도
            if (!plancheCanvas) return;

            // 모바일 체크 (Web Share API 지원 여부)
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            const canShare = navigator.share && navigator.canShare;

            // 모든 이미지를 파일로 변환
            const files = [];

            // 1. 플렁슈 이미지
            const plancheBlob = await new Promise(resolve =>
                plancheCanvas.toBlob(resolve, 'image/png')
            );
            const plancheFile = new File([plancheBlob], `planche_${planche?.planche_number}.png`, { type: 'image/png' });
            files.push(plancheFile);

            // 2. 개별 플래시 이미지 렌더링 (SOLD 제외)
            for (let i = 0; i < flashes.length; i++) {
                const flash = flashes[i];
                // SOLD(unavailable) 플래시는 다운로드 제외
                if (flash.available === false) continue;

                const flashCanvas = await renderSingleFlash(flash, CANVAS.RENDER_SCALE);
                if (flashCanvas) {
                    const flashBlob = await new Promise(resolve =>
                        flashCanvas.toBlob(resolve, 'image/png')
                    );
                    const flashFile = new File([flashBlob], `flash_${flash.id}_${i + 1}.png`, { type: 'image/png' });
                    files.push(flashFile);
                }
            }

            const downloadResult = await deliverFiles(files, {
                preferNativeShare: isMobile && canShare,
                title: `Planche ${planche?.planche_number}`,
                text: `${files.length}개 이미지`
            });

            if (downloadResult.method === 'cancelled') {
                return;
            }
        } catch (err) {
            console.error('Failed to download:', err);
            alert('다운로드에 실패했습니다.');
        }
    };

    // 개별 플래시 이미지 렌더링 함수
    // 개별 플래시 캔버스 렌더링 (다운로드용)
    const renderSingleFlash = async (flash, scale = 1) => {
        const loadImage = (src) => new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });

        try {
            // 캔버스 기본 크기 (상수 사용)
            const baseWidth = CANVAS.BASE_WIDTH;
            const baseHeight = CANVAS.BASE_HEIGHT;

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = baseWidth * scale;
            canvas.height = baseHeight * scale;
            ctx.scale(scale, scale);

            // 흰색 배경
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, baseWidth, baseHeight);

            // 이미지 로드
            const imageSrc = processedImages[flash.id] || croppedImages[flash.id] || flash.image_url;
            const img = await loadImage(imageSrc);

            // singleView 설정 가져오기 (픽셀 기반)
            const viewScale = flash.singleViewScale || 1;

            // 화면 픽셀 → 캔버스 픽셀 스케일 (유틸리티 사용)
            const { x: viewX, y: viewY } = screenOffsetToCanvas(
                flash.singleViewX || 0,
                flash.singleViewY || 0
            );

            // 이미지 그리기 (캔버스 중심 기준 + 오프셋)
            ctx.save();
            const imgWidth = img.naturalWidth;
            const imgHeight = img.naturalHeight;

            // CSS와 동일하게: max-width: 90%, max-height: 75%
            const maxDisplayWidth = baseWidth * 0.9;
            const maxDisplayHeight = baseHeight * 0.75;
            const fitScale = Math.min(maxDisplayWidth / imgWidth, maxDisplayHeight / imgHeight);
            const displayWidth = imgWidth * fitScale * viewScale;
            const displayHeight = imgHeight * fitScale * viewScale;

            // 중앙 정렬 + 오프셋
            const centerX = (baseWidth - displayWidth) / 2;
            const centerY = (baseHeight - displayHeight) / 2;
            const drawX = centerX + viewX;
            const drawY = centerY + viewY;

            ctx.drawImage(img, drawX, drawY, displayWidth, displayHeight);
            ctx.restore();

            // 정보 라벨 렌더링 (showInfo가 false가 아닌 경우)
            if (flash.showInfo !== false) {
                const infoFont = flash.infoFont || 'sans-serif';
                const infoWeight = flash.infoWeight === 'normal' ? '400' : flash.infoWeight === 'bold' ? '700' : flash.infoWeight || '300';
                const infoSize = flash.infoSize || 14;
                const infoPosition = flash.infoPosition || flashbookPolicy.layout.info_position;

                ctx.fillStyle = '#333333';
                // CSS에 font-style: italic 있음
                ctx.font = `italic ${infoWeight} ${infoSize}px ${infoFont}`;

                // 정보 텍스트
                const flashNum = parseInt(flash.flash_number?.replace('N.', '') || '0');
                const infoText = `[ FLASH.${flashNum} ] ${flash.size || ''}`;

                // 라벨 위치 계산
                let labelX = 10, labelY = baseHeight - 10;
                if (infoPosition === 'tl') { labelX = 10; labelY = infoSize + 10; }
                else if (infoPosition === 'tr') { labelX = baseWidth - ctx.measureText(infoText).width - 10; labelY = infoSize + 10; }
                else if (infoPosition === 'bl') { labelX = 10; labelY = baseHeight - 10; }
                else if (infoPosition === 'br') { labelX = baseWidth - ctx.measureText(infoText).width - 10; labelY = baseHeight - 10; }
                else if (infoPosition === 'custom') {
                    labelX = flash.customInfoX || 10;
                    labelY = (flash.customInfoY || 10) + infoSize;
                }

                // CSS에 letter-spacing 없으므로 그대로 그리기
                ctx.fillText(infoText, labelX, labelY);
            }

            return canvas;
        } catch (err) {
            console.error('Failed to render flash:', err);
            return null;
        }
    };

    const handleOriginalFlashDownload = async () => {
        const flash = flashes[viewIndex - 1];
        if (!flash?.image_url) return;

        setIsDownloadMenuOpen(false);

        try {
            const originalFile = await fetchUrlAsFile(
                flash.image_url,
                buildFlashDownloadNameBase(flash, 'original')
            );

            const downloadResult = await deliverFiles([originalFile], {
                preferNativeShare: isIOSFamilyDevice(),
                title: `FLASH ${flash.flash_number || flash.id}`,
            });

            if (downloadResult.method === 'cancelled') {
                return;
            }
        } catch (error) {
            console.error('Failed to download original flash:', error);
            alert('원본 다운로드에 실패했습니다.');
        }
    };

    const handleCanvasFlashDownload = async () => {
        const flash = flashes[viewIndex - 1];
        if (!flash) return;

        setIsDownloadMenuOpen(false);

        try {
            const flashCanvas = await renderSingleFlash(flash, CANVAS.RENDER_SCALE);
            if (!flashCanvas) {
                throw new Error('Failed to render flash canvas.');
            }

            const canvasFile = await canvasToFile(
                flashCanvas,
                buildFlashDownloadNameBase(flash, 'canvas'),
                { type: 'image/png' }
            );

            const downloadResult = await deliverFiles([canvasFile], {
                preferNativeShare: isIOSFamilyDevice(),
                title: `FLASH ${flash.flash_number || flash.id}`,
            });

            if (downloadResult.method === 'cancelled') {
                return;
            }
        } catch (error) {
            console.error('Failed to download flash canvas:', error);
            alert('캔버스 다운로드에 실패했습니다.');
        }
    };

    // 스와이프 핸들러 (뷰 전환용)
    const handleSwipeStart = (e) => {
        // 드래그 중이거나 이미지를 선택 중이면 스와이프 무시
        if (dragType) return;
        // flashItem 위에서 터치 시작하면 스와이프 무시 (플렁슈 뷰)
        if (e.target.closest(`.${styles.flashItem}`)) return;
        // 개별 플래시 뷰 내에서 터치 시작하면 스와이프 무시
        if (e.target.closest(`.${styles.singleFlashView}`)) return;
        const touch = e.touches[0];
        setSwipeStartX(touch.clientX);
    };

    const handleSwipeEnd = (e) => {
        if (swipeStartX === null) return;
        const touch = e.changedTouches[0];
        const diffX = touch.clientX - swipeStartX;
        const minSwipeDistance = 50;

        if (Math.abs(diffX) > minSwipeDistance) {
            const totalViews = flashes.length + 1;
            if (diffX < 0) {
                // 왼쪽으로 스와이프 → 다음 뷰
                setSlideDirection('right');
                setViewIndex(prev => Math.min(prev + 1, totalViews - 1));
            } else {
                // 오른쪽으로 스와이프 → 이전 뷰
                setSlideDirection('left');
                setViewIndex(prev => Math.max(prev - 1, 0));
            }
        }
        setSwipeStartX(null);
    };

    // 트랙패드 두 손가락 스와이프 핸들러 (데스크톱용)
    const lastWheelTime = useRef(0);
    const handleTrackpadSwipe = (e) => {
        // 수평 스크롤만 처리 (deltaX가 deltaY보다 클 때)
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

        // 디바운스 (300ms)
        const now = Date.now();
        if (now - lastWheelTime.current < 300) return;

        const threshold = 30;
        if (Math.abs(e.deltaX) > threshold) {
            const totalViews = flashes.length + 1;
            if (e.deltaX > 0) {
                // 오른쪽으로 스크롤 → 다음 뷰
                setSlideDirection('right');
                setViewIndex(prev => Math.min(prev + 1, totalViews - 1));
            } else {
                // 왼쪽으로 스크롤 → 이전 뷰
                setSlideDirection('left');
                setViewIndex(prev => Math.max(prev - 1, 0));
            }
            lastWheelTime.current = now;
        }
    };

    // 키보드 화살표로 뷰 네비게이션
    useEffect(() => {
        const handleKeyDown = (e) => {
            // 입력 필드에 포커스가 있으면 무시
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                return;
            }
            // 크롭 모달이 열려있으면 무시
            if (showCropModal) return;

            const totalViews = flashes.length + 1;

            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (viewIndex > 0) {
                    setSlideDirection('left');
                    setViewIndex(prev => prev - 1);
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (viewIndex < totalViews - 1) {
                    setSlideDirection('right');
                    setViewIndex(prev => prev + 1);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [viewIndex, flashes.length, showCropModal]);

    if (loading) {
        return <div className={styles.container}><div className={styles.loading}>Loading...</div></div>;
    }

    return (
        <div className={styles.container}>
            {/* 헤더 */}
            <div className={styles.header}>
                <button className={styles.backButton} onClick={() => navigate('/flashbook')}>
                    ← Back
                </button>
                <h1 className={styles.title}>PLANCHE {planche?.planche_number}</h1>
                <div className={styles.actions}>
                    <button
                        className={`${styles.iconButton} ${isSaving ? styles.saving : ''}`}
                        onClick={handleSave}
                        title="Save"
                        disabled={isSaving}
                        style={{ position: 'relative' }}
                    >
                        {isSaving ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" className={styles.spinner}>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4 31.4" />
                            </svg>
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                                {hasUnsavedChanges && (
                                    <span className={styles.unsavedDot} />
                                )}
                            </>
                        )}
                    </button>
                    <button className={styles.iconButton} onClick={handleDownload} title="Download">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </button>
                    <button
                        className={styles.iconButton}
                        onClick={handleDelete}
                        title="Delete Planche"
                        style={{ color: '#ef4444' }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* 저장 상태 인디케이터 - 헤더 아래 중앙 */}
            {saveStatus && (
                <div className={`${styles.saveIndicator} ${styles[saveStatus]}`}>
                    {saveStatus === 'saved' ? (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            저장됨
                        </>
                    ) : (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                            저장 실패
                        </>
                    )}
                </div>
            )}

            {/* 캔버스 (3:4 비율) */}
            <div
                ref={canvasWrapperRef}
                className={`${styles.canvasWrapper} ${isDraggingFile ? styles.dropActive : ''}`}
                onTouchStart={handleSwipeStart}
                onTouchEnd={handleSwipeEnd}
                onWheel={handleTrackpadSwipe}
                {...getDropZoneProps()}
            >
                {/* 드래그 앤 드롭 오버레이 */}
                {isDraggingFile && (
                    <div className={styles.dropOverlay}>
                        <div className={styles.dropContent}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <p>이미지를 여기에 드롭하세요</p>
                        </div>
                    </div>
                )}
                {/* 스케일러: 뷰포트에 맞게 축소/확대 */}
                <div ref={scalerRef} className={styles.canvasScaler}>
                    {/* 캔버스: 항상 400x533px 고정 */}
                    <div
                        ref={canvasRef}
                        className={styles.canvas}
                        onClick={(e) => {
                            // flashItem을 클릭한 게 아니면 선택 해제
                            if (!e.target.closest(`.${styles.flashItem}`)) {
                                setSelectedFlash(null);
                            }
                        }}
                    >
                        {viewIndex === 0 ? (
                            /* 플렁슈 전체 뷰 */
                            <div
                                key="planche-view"
                                className={slideDirection === 'left' ? styles.slideLeft : ''}
                                style={{ width: '100%', height: '100%', position: 'relative' }}
                            >
                                {flashes.map((flash) => (
                                    <div
                                        key={flash.id}
                                        className={`${styles.flashItem} ${selectedFlash?.id === flash.id ? styles.selected : ''} ${!flash.available && deactivationStyle === 'dim' ? styles.dim : ''}`}
                                        style={{
                                            left: flash.x || 0,
                                            top: flash.y || 0,
                                            transform: `scale(${flash.scale || 1}) rotate(${flash.rotation || 0}deg)`
                                        }}
                                        onMouseDown={(e) => handleMouseDown(e, flash)}
                                        onTouchStart={(e) => handleTouchStart(e, flash)}
                                        onWheel={(e) => handleWheel(e, flash)}
                                    >
                                        <img
                                            src={processedImages[flash.id] || croppedImages[flash.id] || flash.image_url}
                                            alt={flash.name}
                                            className={styles.flashImage}
                                            draggable={false}
                                        />
                                        {/* SOLD 오버레이 (DOM 요소로 렌더링 - pseudo-element 대신) */}
                                        {!flash.available && deactivationStyle !== 'dim' && (
                                            <div
                                                key={`sold-${flash.id}-${deactivationStyle}`}
                                                className={styles.soldOverlay}
                                                style={{
                                                    // 부모 flashItem의 회전을 상쇄하여 정 각도 유지
                                                    transform: `rotate(${-(flash.rotation || 0)}deg)`
                                                }}
                                            >
                                                {(deactivationStyle === 'diagonal' || deactivationStyle === 'cross') && (
                                                    <div
                                                        className={styles.soldLine}
                                                        style={{
                                                            width: `${lineLength}%`,
                                                            height: `${lineWidth / (flash.scale || 1)}px`,
                                                            backgroundColor: lineColor,
                                                            transform: 'translate(-50%, -50%) rotate(135deg)'
                                                        }}
                                                    />
                                                )}
                                                {deactivationStyle === 'cross' && (
                                                    <div
                                                        className={styles.soldLine}
                                                        style={{
                                                            width: `${lineLength}%`,
                                                            height: `${lineWidth / (flash.scale || 1)}px`,
                                                            backgroundColor: lineColor,
                                                            transform: 'translate(-50%, -50%) rotate(45deg)'
                                                        }}
                                                    />
                                                )}
                                                {deactivationStyle === 'text' && (
                                                    <div
                                                        className={styles.soldText}
                                                        style={{
                                                            color: soldTextColor,
                                                            fontWeight: soldTextWeight,
                                                            fontStyle: soldTextItalic ? 'italic' : 'normal',
                                                            transform: `translate(-50%, -50%) rotate(${soldTextRotation}deg)`,
                                                            fontSize: `${soldTextSize}cqi`,
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {soldText}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {showLabel && <div
                                    className={styles.plancheLabel}
                                    style={{
                                        fontFamily: labelFont,
                                        fontWeight: labelWeight,
                                        fontSize: `${labelSize}px`,
                                        ...(labelPosition === 'custom' ? {
                                            left: customLabelPos.x,
                                            top: customLabelPos.y,
                                        } : {})
                                    }}
                                    data-position={labelPosition}
                                    onMouseDown={labelPosition === 'custom' ? (e) => {
                                        e.stopPropagation();
                                        setIsDraggingLabel(true);
                                        if (!canvasRef.current) return;
                                        const rect = canvasRef.current.getBoundingClientRect();
                                        setDragOffset({
                                            x: e.clientX - rect.left - customLabelPos.x,
                                            y: e.clientY - rect.top - customLabelPos.y
                                        });
                                    } : undefined}
                                >
                                    PLANCHE {planche?.planche_number}
                                </div>}
                            </div>
                        ) : (
                            /* 개별 플래시 뷰 */
                            flashes[viewIndex - 1] && (
                                <div
                                    key={viewIndex}
                                    ref={singleViewRef}
                                    className={`${styles.singleFlashView} ${slideDirection === 'right' ? styles.slideRight : styles.slideLeft}`}
                                    onMouseDown={(e) => {
                                        // 이미지 드래그 시작 (라벨 드래그가 아닐 때)
                                        if (e.target.classList.contains(styles.singleFlashImage)) {
                                            setDragType('singleView');
                                            const flash = flashes[viewIndex - 1];
                                            setDragOffset({
                                                x: e.clientX - (flash.singleViewX || 0),
                                                y: e.clientY - (flash.singleViewY || 0)
                                            });
                                        }
                                    }}
                                    onMouseMove={(e) => {
                                        const flash = flashes[viewIndex - 1];
                                        // 라벨 드래그
                                        if (isDraggingLabel && flash.infoPosition === 'custom' && singleViewRef.current) {
                                            const rect = singleViewRef.current.getBoundingClientRect();
                                            const newX = e.clientX - rect.left - dragOffset.x;
                                            const newY = e.clientY - rect.top - dragOffset.y;
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flash.id ? { ...f, customInfoX: newX, customInfoY: newY } : f
                                            ));
                                        }
                                        // 이미지 드래그 (픽셀 기반 저장)
                                        if (dragType === 'singleView') {
                                            const newX = e.clientX - dragOffset.x;
                                            const newY = e.clientY - dragOffset.y;
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flash.id ? { ...f, singleViewX: newX, singleViewY: newY } : f
                                            ));
                                        }
                                    }}
                                    onMouseUp={() => {
                                        setIsDraggingLabel(false);
                                        setDragType(null);
                                    }}
                                    onMouseLeave={() => {
                                        setIsDraggingLabel(false);
                                        setDragType(null);
                                    }}
                                    /* 터치 이벤트 (모바일) */
                                    onTouchStart={(e) => {
                                        e.preventDefault(); // 스와이프 방지
                                        const flash = flashes[viewIndex - 1];
                                        // 이미지를 터치했을 때만 드래그 시작
                                        const isImageTouch = e.target.classList.contains(styles.singleFlashImage);
                                        if (e.touches.length === 1 && isImageTouch) {
                                            // 싱글 터치: 드래그
                                            const touch = e.touches[0];
                                            setDragType('singleView');
                                            setDragOffset({
                                                x: touch.clientX - (flash.singleViewX || 0),
                                                y: touch.clientY - (flash.singleViewY || 0)
                                            });
                                        } else if (e.touches.length === 2) {
                                            // 투 핑거: 핀치 줌 준비
                                            const dx = e.touches[0].clientX - e.touches[1].clientX;
                                            const dy = e.touches[0].clientY - e.touches[1].clientY;
                                            setLastTouchDistance(Math.sqrt(dx * dx + dy * dy));
                                        }
                                    }}
                                    onTouchMove={(e) => {
                                        const flash = flashes[viewIndex - 1];
                                        if (e.touches.length === 1 && dragType === 'singleView') {
                                            // 싱글 터치: 드래그 (픽셀 기반 저장)
                                            const touch = e.touches[0];
                                            const newX = touch.clientX - dragOffset.x;
                                            const newY = touch.clientY - dragOffset.y;
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flash.id ? { ...f, singleViewX: newX, singleViewY: newY } : f
                                            ));
                                        } else if (e.touches.length === 2 && lastTouchDistance) {
                                            // 투 핑거: 핀치 줌
                                            e.preventDefault();
                                            const dx = e.touches[0].clientX - e.touches[1].clientX;
                                            const dy = e.touches[0].clientY - e.touches[1].clientY;
                                            const distance = Math.sqrt(dx * dx + dy * dy);
                                            const scaleDelta = (distance - lastTouchDistance) * 0.01;
                                            const newScale = Math.max(0.1, (flash.singleViewScale || 1) + scaleDelta);
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flash.id ? { ...f, singleViewScale: newScale } : f
                                            ));
                                            setLastTouchDistance(distance);
                                        }
                                    }}
                                    onTouchEnd={() => {
                                        setDragType(null);
                                        setLastTouchDistance(null);
                                    }}
                                    onWheel={(e) => {
                                        // 휠 줌 - 이미지에서만 적용 (트랙패드/마우스 휠 정규화)
                                        if (e.target.classList.contains(styles.singleFlashImage)) {
                                            e.preventDefault();
                                            const flash = flashes[viewIndex - 1];
                                            const currentScale = flash.singleViewScale || 1;
                                            // deltaY를 정규화하여 트랙패드와 마우스 휠 모두 부드럽게 동작
                                            const normalizedDelta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY) * 0.001, 0.05);
                                            const newScale = Math.max(0.1, Math.min(5, currentScale - normalizedDelta));
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flash.id ? { ...f, singleViewScale: newScale } : f
                                            ));
                                        }
                                    }}
                                >
                                    <img
                                        src={croppedImages[flashes[viewIndex - 1].id] || flashes[viewIndex - 1].image_url}
                                        alt={flashes[viewIndex - 1].name}
                                        className={styles.singleFlashImage}
                                        style={{
                                            // 정규화 좌표(400x533 기준)를 픽셀로 직접 표시
                                            // translate에서는 px 사용 (singleViewRef에서 스케일 적용)
                                            transform: `translate(${flashes[viewIndex - 1].singleViewX || 0}px, ${flashes[viewIndex - 1].singleViewY || 0}px) scale(${flashes[viewIndex - 1].singleViewScale || 1})`,
                                            cursor: 'move',
                                            border: dragType === 'singleView' ? '2px solid #22c55e' : '2px solid transparent',
                                            borderRadius: '4px'
                                        }}
                                        draggable={false}
                                    />
                                    {/* 정보 라벨 오버레이 */}
                                    {flashes[viewIndex - 1].showInfo !== false && (
                                        <div
                                            className={styles.flashInfoLabel}
                                            data-position={flashes[viewIndex - 1].infoPosition || flashbookPolicy.layout.info_position}
                                            style={{
                                                fontFamily: flashes[viewIndex - 1].infoFont || 'sans-serif',
                                                fontWeight: flashes[viewIndex - 1].infoWeight || '300',
                                                fontSize: `${flashes[viewIndex - 1].infoSize || 14}px`,
                                                ...(flashes[viewIndex - 1].infoPosition === 'custom' ? {
                                                    left: flashes[viewIndex - 1].customInfoX || 10,
                                                    top: flashes[viewIndex - 1].customInfoY || 10,
                                                } : {}),
                                                cursor: flashes[viewIndex - 1].infoPosition === 'custom' ? 'move' : 'default'
                                            }}
                                            onMouseDown={flashes[viewIndex - 1].infoPosition === 'custom' ? (e) => {
                                                e.stopPropagation();
                                                setIsDraggingLabel(true);
                                                if (!singleViewRef.current) return;
                                                const rect = singleViewRef.current.getBoundingClientRect();
                                                setDragOffset({
                                                    x: e.clientX - rect.left - (flashes[viewIndex - 1].customInfoX || 10),
                                                    y: e.clientY - rect.top - (flashes[viewIndex - 1].customInfoY || 10)
                                                });
                                            } : undefined}
                                        >
                                            [ FLASH.{parseInt(flashes[viewIndex - 1].flash_number?.replace('N.', '') || '0')} ] {flashes[viewIndex - 1].size || ''}
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </div>  {/* canvas 닫기 */}
                </div>  {/* canvasScaler 닫기 */}

                {/* 페이지네이션 - 슬라이더 스타일 피커 */}
                <div className={styles.pagination}>
                    <div
                        className={styles.scrollPicker}
                        ref={scrollPickerRef}
                        onTouchStart={(e) => {
                            const container = scrollPickerRef.current;
                            if (!container) return;
                            const rect = container.getBoundingClientRect();
                            const touchX = e.touches[0].clientX - rect.left;
                            const totalDots = flashes.length + 1;
                            const dotWidth = rect.width / totalDots;
                            const newIndex = Math.min(Math.max(0, Math.round(touchX / dotWidth - 0.5)), totalDots - 1);
                            if (newIndex !== viewIndex) {
                                setSlideDirection(newIndex > viewIndex ? 'right' : 'left');
                                setViewIndex(newIndex);
                            }
                        }}
                        onTouchMove={(e) => {
                            const container = scrollPickerRef.current;
                            if (!container) return;
                            const rect = container.getBoundingClientRect();
                            const touchX = e.touches[0].clientX - rect.left;
                            const totalDots = flashes.length + 1;
                            const dotWidth = rect.width / totalDots;
                            const newIndex = Math.min(Math.max(0, Math.round(touchX / dotWidth - 0.5)), totalDots - 1);
                            if (newIndex !== viewIndex) {
                                setSlideDirection(newIndex > viewIndex ? 'right' : 'left');
                                setViewIndex(newIndex);
                            }
                        }}
                        onMouseDown={(e) => {
                            // 마우스 드래그 시작
                            const container = scrollPickerRef.current;
                            if (!container) return;
                            container.dataset.dragging = 'true';
                            const rect = container.getBoundingClientRect();
                            const mouseX = e.clientX - rect.left;
                            const totalDots = flashes.length + 1;
                            const dotWidth = rect.width / totalDots;
                            const newIndex = Math.min(Math.max(0, Math.round(mouseX / dotWidth - 0.5)), totalDots - 1);
                            if (newIndex !== viewIndex) {
                                setSlideDirection(newIndex > viewIndex ? 'right' : 'left');
                                setViewIndex(newIndex);
                            }
                        }}
                        onMouseMove={(e) => {
                            // 마우스 드래그 중
                            const container = scrollPickerRef.current;
                            if (!container || container.dataset.dragging !== 'true') return;
                            const rect = container.getBoundingClientRect();
                            const mouseX = e.clientX - rect.left;
                            const totalDots = flashes.length + 1;
                            const dotWidth = rect.width / totalDots;
                            const newIndex = Math.min(Math.max(0, Math.round(mouseX / dotWidth - 0.5)), totalDots - 1);
                            if (newIndex !== viewIndex) {
                                setSlideDirection(newIndex > viewIndex ? 'right' : 'left');
                                setViewIndex(newIndex);
                            }
                        }}
                        onMouseUp={() => {
                            const container = scrollPickerRef.current;
                            if (container) container.dataset.dragging = 'false';
                        }}
                        onMouseLeave={() => {
                            const container = scrollPickerRef.current;
                            if (container) container.dataset.dragging = 'false';
                        }}
                        onWheel={(e) => {
                            // 트랙패드/마우스 휠 좌우 스크롤
                            e.preventDefault();
                            e.stopPropagation();

                            // 시간 기반 debounce (200ms)
                            const now = Date.now();
                            const lastWheel = scrollPickerRef.current?.dataset?.lastWheel || 0;
                            if (now - lastWheel < 200) return;
                            if (scrollPickerRef.current) {
                                scrollPickerRef.current.dataset.lastWheel = now;
                            }

                            const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
                            const totalDots = flashes.length + 1;

                            // 휠 감도 조절 (낮춤)
                            if (Math.abs(delta) < 10) return;

                            // delta > 0: 오른쪽으로 스크롤 → 이전 페이지
                            // delta < 0: 왼쪽으로 스크롤 → 다음 페이지
                            const newIndex = delta > 0
                                ? Math.max(viewIndex - 1, 0)
                                : Math.min(viewIndex + 1, totalDots - 1);

                            if (newIndex !== viewIndex) {
                                setSlideDirection(newIndex > viewIndex ? 'right' : 'left');
                                setViewIndex(newIndex);
                            }
                        }}
                    >
                        {[...Array(flashes.length + 1)].map((_, idx) => (
                            <div
                                key={idx}
                                className={`${styles.pickerItem} ${viewIndex === idx ? styles.active : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSlideDirection(idx > viewIndex ? 'right' : 'left');
                                    setViewIndex(idx);
                                }}
                            />
                        ))}
                    </div>
                    {/* 플래시 추가 버튼 */}
                    <button
                        className={styles.addFlashButton}
                        onClick={() => fileInputRef.current?.click()}
                        title="Add Flash"
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                </div>
            </div>  {/* canvasWrapper 닫기 */}

            {/* 선택된 플래시 인라인 설정 - 플렁슈 전체 뷰에서만 표시 (캔버스 바로 아래) */}
            {
                selectedFlash && viewIndex === 0 && (
                    <div className={styles.selectedInfo}>
                        <div className={styles.infoRow}>
                            <span className={styles.flashNum}>{selectedFlash.flash_number}</span>
                            <button
                                className={`${styles.statusBtn} ${selectedFlash.available !== false ? styles.available : styles.sold}`}
                                onClick={() => {
                                    setFlashes(prev => prev.map(f =>
                                        f.id === selectedFlash.id ? { ...f, available: !f.available } : f
                                    ));
                                }}
                            >
                                {selectedFlash.available !== false ? 'Avail' : 'Sold'}
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <button
                                    className={styles.iconBtn}
                                    onMouseDown={() => startHoldAction(() => handleScaleChange(-0.1))}
                                    onMouseUp={stopHoldAction}
                                    onMouseLeave={stopHoldAction}
                                    onTouchStart={() => startHoldAction(() => handleScaleChange(-0.1))}
                                    onTouchEnd={stopHoldAction}
                                    title="축소"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                                <span style={{ fontSize: '11px', minWidth: '35px', textAlign: 'center', fontFamily: 'monospace' }}>
                                    {(selectedFlash.scale || 1).toFixed(1)}x
                                </span>
                                <button
                                    className={styles.iconBtn}
                                    onMouseDown={() => startHoldAction(() => handleScaleChange(0.1))}
                                    onMouseUp={stopHoldAction}
                                    onMouseLeave={stopHoldAction}
                                    onTouchStart={() => startHoldAction(() => handleScaleChange(0.1))}
                                    onTouchEnd={stopHoldAction}
                                    title="확대"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                </button>
                            </div>
                            <button
                                className={styles.iconBtn}
                                onMouseDown={() => startHoldAction(() => handleRotate(5))}
                                onMouseUp={stopHoldAction}
                                onMouseLeave={stopHoldAction}
                                onTouchStart={() => startHoldAction(() => handleRotate(5))}
                                onTouchEnd={stopHoldAction}
                                title={`회전: ${Math.round(selectedFlash.rotation || 0)}°`}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 12a9 9 0 1 1-3-6.71" />
                                    <polyline points="21 3 21 9 15 9" />
                                </svg>
                            </button>
                            <button
                                className={styles.iconBtn}
                                onClick={async () => {
                                    if (window.confirm(`${selectedFlash.flash_number} 삭제하시겠습니까?`)) {
                                        try {
                                            await flashbookApi.deleteFlash(selectedFlash.id);
                                            setFlashes(prev => prev.filter(f => f.id !== selectedFlash.id));
                                            setSelectedFlash(null);
                                        } catch (error) {
                                            console.error('플래시 삭제 실패:', error);
                                            alert(t('flashbook.deleteFailed'));
                                        }
                                    }
                                }}
                                title="삭제"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )
            }

            {/* 컨트롤 패널 (캔버스 하단) */}
            <div className={styles.controls}>
                {viewIndex === 0 ? (
                    /* 플렁슈 전체 뷰 설정 */
                    <>
                        <div className={styles.styleSelector}>
                            <span>SOLD</span>
                            <select
                                value={deactivationStyle}
                                onChange={(e) => handleStyleChange(e.target.value)}
                            >
                                <option value="dim">DIM</option>
                                <option value="diagonal">LINE</option>
                                <option value="cross">X</option>
                                <option value="text">TEXT</option>
                            </select>
                            {(deactivationStyle === 'diagonal' || deactivationStyle === 'cross') && (
                                <>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={lineWidth}
                                        onChange={(e) => setLineWidth(Number(e.target.value))}
                                        title={`두께: ${lineWidth}px`}
                                        className={styles.rangeInput}
                                    />
                                    <input
                                        type="range"
                                        min="30"
                                        max="150"
                                        value={lineLength}
                                        onChange={(e) => setLineLength(Number(e.target.value))}
                                        title={`길이: ${lineLength}%`}
                                        className={styles.rangeInput}
                                    />
                                    <input
                                        type="color"
                                        value={lineColor}
                                        onChange={(e) => setLineColor(e.target.value)}
                                        title="선 색상"
                                        className={styles.colorInput}
                                    />
                                </>
                            )}
                            {deactivationStyle === 'text' && (
                                <>
                                    <input
                                        type="text"
                                        value={soldText}
                                        onChange={(e) => setSoldText(e.target.value)}
                                        placeholder="TAKEN"
                                        className={styles.textInput}
                                        style={{ width: '60px', fontSize: '12px' }}
                                    />
                                    <input
                                        type="color"
                                        value={soldTextColor}
                                        onChange={(e) => setSoldTextColor(e.target.value)}
                                        title="텍스트 색상"
                                        className={styles.colorInput}
                                    />
                                    <input
                                        type="range"
                                        min="10"
                                        max="40"
                                        value={soldTextSize}
                                        onChange={(e) => setSoldTextSize(Number(e.target.value))}
                                        title={`크기: ${soldTextSize}`}
                                        className={styles.rangeInput}
                                    />
                                    <input
                                        type="range"
                                        min="100"
                                        max="900"
                                        step="100"
                                        value={soldTextWeight}
                                        onChange={(e) => setSoldTextWeight(Number(e.target.value))}
                                        title={`굵기: ${soldTextWeight}`}
                                        className={styles.rangeInput}
                                    />
                                    <input
                                        type="range"
                                        min="-45"
                                        max="45"
                                        value={soldTextRotation}
                                        onChange={(e) => setSoldTextRotation(Number(e.target.value))}
                                        title={`회전: ${soldTextRotation}°`}
                                        className={styles.rangeInput}
                                    />
                                    <button
                                        onClick={() => setSoldTextItalic(!soldTextItalic)}
                                        className={styles.iconBtn}
                                        style={{
                                            fontStyle: 'italic',
                                            fontWeight: soldTextItalic ? 'bold' : 'normal',
                                            backgroundColor: soldTextItalic ? '#e0e0e0' : 'transparent'
                                        }}
                                        title="기울기 (Italic)"
                                    >
                                        I
                                    </button>
                                </>
                            )}
                        </div>
                        <div className={styles.styleSelector}>
                            <label><input type="checkbox" checked={showLabel} onChange={(e) => setShowLabel(e.target.checked)} /> TITLE</label>
                            <select
                                value={labelPosition}
                                onChange={(e) => setLabelPosition(e.target.value)}
                            >
                                <option value="tl">↖</option>
                                <option value="tr">↗</option>
                                <option value="bl">↙</option>
                                <option value="br">↘</option>
                                <option value="custom">✋</option>
                            </select>
                            <select
                                value={labelFont}
                                onChange={(e) => setLabelFont(e.target.value)}
                            >
                                <option value="sans-serif">Sans</option>
                                <option value="serif">Serif</option>
                                <option value="monospace">Mono</option>
                                <option value="cursive">Cursive</option>
                            </select>
                            <select
                                value={labelWeight}
                                onChange={(e) => setLabelWeight(e.target.value)}
                            >
                                <option value="normal">Normal</option>
                                <option value="bold">Bold</option>
                                <option value="lighter">Light</option>
                            </select>
                            <select
                                value={labelSize}
                                onChange={(e) => setLabelSize(e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="12">12</option>
                                <option value="14">14</option>
                                <option value="16">16</option>
                                <option value="18">18</option>
                                <option value="20">20</option>
                            </select>
                        </div>
                        <div className={styles.styleSelector}>
                            <span>BG</span>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '10px' }}>
                                <input
                                    type="radio"
                                    name="bg"
                                    checked={bgMode === 'none'}
                                    onChange={() => setBgMode('none')}
                                />
                                None
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '10px' }}>
                                <input
                                    type="radio"
                                    name="bg"
                                    checked={bgMode === 'white'}
                                    onChange={() => setBgMode('white')}
                                />
                                White
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '10px' }}>
                                <input
                                    type="radio"
                                    name="bg"
                                    checked={bgMode === 'custom'}
                                    onChange={() => setBgMode('custom')}
                                />
                                Custom
                            </label>
                            {bgMode === 'custom' && (
                                <input
                                    type="color"
                                    value={customBgColor}
                                    onChange={(e) => setCustomBgColor(e.target.value)}
                                    style={{ width: '20px', height: '20px', padding: 0, border: 'none' }}
                                />
                            )}
                        </div>
                        <div className={styles.tips}>
                            📱 이동 | 핀치: 크기/회전 | PC: R키 회전
                        </div>
                    </>
                ) : (
                    /* 개별 플래시 뷰 설정 */
                    flashes[viewIndex - 1] && (
                        <div className={styles.flashControls}>
                            {/* Row 1: 액션 버튼 */}
                            <div className={styles.infoRow}>
                                <button
                                    className={`${styles.statusBtn} ${flashes[viewIndex - 1].available !== false ? styles.available : styles.sold}`}
                                    onClick={() => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, available: !f.available } : f
                                        ));
                                    }}
                                >
                                    {flashes[viewIndex - 1].available !== false ? 'Avail' : 'Sold'}
                                </button>
                                {/* 이미지 교체 버튼 */}
                                <input
                                    type="file"
                                    ref={replaceFileInputRef}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleReplaceFlash}
                                />
                                <button
                                    className={styles.iconBtn}
                                    onClick={() => replaceFileInputRef.current?.click()}
                                    disabled={isReplacingFlash}
                                    title="이미지 교체"
                                >
                                    {isReplacingFlash ? (
                                        <span style={{ fontSize: '12px' }}>...</span>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 1 1-3-6.71" />
                                            <polyline points="21 3 21 9 15 9" />
                                        </svg>
                                    )}
                                </button>
                                {/* 다운로드 드롭다운 */}
                                <div
                                    className={styles.downloadDropdown}
                                    data-open={isDownloadMenuOpen ? 'true' : 'false'}
                                >
                                    <button
                                        className={styles.iconBtn}
                                        onClick={() => {
                                            setIsDownloadMenuOpen(prev => !prev);
                                        }}
                                        title="다운로드"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </button>
                                    <div className={styles.dropdownMenu}>
                                        <button
                                            onClick={handleOriginalFlashDownload}
                                        >
                                            {t('flashbook.downloadOriginal')}
                                        </button>
                                        <button
                                            onClick={handleCanvasFlashDownload}
                                        >
                                            {t('flashbook.downloadCanvas')}
                                        </button>
                                    </div>
                                </div>
                                <button
                                    className={styles.iconBtn}
                                    onClick={() => {
                                        setCropFlashId(flashes[viewIndex - 1].id);
                                        setCrop({ unit: '%', width: 50, height: 50, x: 25, y: 25 });
                                        setCompletedCrop(null);
                                        setShowCropModal(true);
                                    }}
                                    title="크롭"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M6 2v14a2 2 0 0 0 2 2h14" />
                                        <path d="M18 22V8a2 2 0 0 0-2-2H2" />
                                    </svg>
                                </button>
                                <button
                                    className={styles.iconBtn}
                                    onClick={async () => {
                                        const flash = flashes[viewIndex - 1];
                                        if (window.confirm(`${flash.flash_number} 삭제하시겠습니까?`)) {
                                            try {
                                                await flashbookApi.deleteFlash(flash.id);
                                                setFlashes(prev => prev.filter(f => f.id !== flash.id));
                                                setViewIndex(0);
                                            } catch (error) {
                                                console.error('플래시 삭제 실패:', error);
                                                alert(t('flashbook.deleteFailed'));
                                            }
                                        }
                                    }}
                                    title="삭제"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                </button>
                            </div>
                            {/* Row 3: 이름 입력 */}
                            <div className={styles.inputRow}>
                                <label>{t('flashbook.flashName')}</label>
                                <input
                                    type="text"
                                    placeholder=""
                                    value={flashes[viewIndex - 1].name || ''}
                                    onChange={(e) => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        const newName = e.target.value;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, name: newName } : f
                                        ));
                                    }}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            await handleSave();
                                            setSaveMessage(t('flashbook.nameSaved'));
                                            setTimeout(() => setSaveMessage(null), 2000);
                                        }
                                    }}
                                    className={styles.inlineInput}
                                />
                            </div>
                            {/* Row 4: 사이즈 입력 */}
                            <div className={styles.inputRow}>
                                <label>{t('flashbook.size')}</label>
                                <div className={styles.inputWithButton}>
                                    <input
                                        type="text"
                                        placeholder="5x7cm"
                                        value={flashes[viewIndex - 1].size || ''}
                                        onChange={(e) => {
                                            const flashId = flashes[viewIndex - 1].id;
                                            const newSize = e.target.value;
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flashId ? { ...f, size: newSize } : f
                                            ));
                                        }}
                                        onKeyDown={async (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                await handleSave();
                                                setSaveMessage(t('flashbook.sizeSaved'));
                                                setTimeout(() => setSaveMessage(null), 2000);
                                            }
                                        }}
                                        className={styles.inlineInput}
                                    />
                                    <button
                                        type="button"
                                        className={styles.tildeBtn}
                                        onClick={() => {
                                            const flashId = flashes[viewIndex - 1].id;
                                            const currentSize = flashes[viewIndex - 1].size || '';
                                            setFlashes(prev => prev.map(f =>
                                                f.id === flashId ? { ...f, size: currentSize + '~' } : f
                                            ));
                                        }}
                                        title="물결(~) 추가"
                                    >
                                        ~
                                    </button>
                                </div>
                            </div>
                            {/* Row 5: 정보표시설정 */}
                            <div className={styles.inputRow}>
                                <label>{t('flashbook.showInfo')}</label>
                                <button
                                    className={`${styles.miniBtn} ${flashes[viewIndex - 1].showInfo !== false ? styles.active : ''}`}
                                    onClick={() => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, showInfo: f.showInfo === false ? true : false } : f
                                        ));
                                    }}
                                >
                                    {flashes[viewIndex - 1].showInfo !== false ? t('flashbook.visible') : t('flashbook.hidden')}
                                </button>
                                <select
                                    value={flashes[viewIndex - 1].infoPosition || flashbookPolicy.layout.info_position}
                                    onChange={(e) => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, infoPosition: e.target.value } : f
                                        ));
                                    }}
                                    className={styles.miniSelect}
                                >
                                    <option value="tl">↖</option>
                                    <option value="tr">↗</option>
                                    <option value="bl">↙</option>
                                    <option value="br">↘</option>
                                    <option value="custom">✋</option>
                                </select>
                                <select
                                    value={flashes[viewIndex - 1].infoFont || 'sans-serif'}
                                    onChange={(e) => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, infoFont: e.target.value } : f
                                        ));
                                    }}
                                    className={styles.miniSelect}
                                >
                                    <option value="sans-serif">Sans</option>
                                    <option value="serif">Serif</option>
                                    <option value="monospace">Mono</option>
                                </select>
                                <select
                                    value={flashes[viewIndex - 1].infoWeight || '300'}
                                    onChange={(e) => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, infoWeight: e.target.value } : f
                                        ));
                                    }}
                                    className={styles.miniSelect}
                                >
                                    <option value="300">Light</option>
                                    <option value="normal">Normal</option>
                                    <option value="bold">Bold</option>
                                </select>
                                <select
                                    value={flashes[viewIndex - 1].infoSize || '14'}
                                    onChange={(e) => {
                                        const flashId = flashes[viewIndex - 1].id;
                                        setFlashes(prev => prev.map(f =>
                                            f.id === flashId ? { ...f, infoSize: e.target.value } : f
                                        ));
                                    }}
                                    className={styles.miniSelect}
                                >
                                    <option value="8">8</option>
                                    <option value="10">10</option>
                                    <option value="12">12</option>
                                    <option value="14">14</option>
                                </select>
                            </div>
                        </div>
                    )
                )}
            </div>

            {/* 숨겨진 파일 입력 */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
            />

            {/* 크롭 모달 */}
            {
                showCropModal && cropFlashId && (
                    <div className={styles.cropModal}>
                        <div className={styles.cropModalContent}>
                            <div className={styles.cropModalHeader}>
                                <h3>이미지 크롭</h3>
                                <div className={styles.cropActions}>
                                    <button
                                        className={styles.cropCancelBtn}
                                        onClick={() => {
                                            setShowCropModal(false);
                                            setCropFlashId(null);
                                        }}
                                    >
                                        취소
                                    </button>
                                    <button
                                        className={styles.cropApplyBtn}
                                        onClick={applyCrop}
                                        disabled={!completedCrop}
                                    >
                                        적용
                                    </button>
                                </div>
                            </div>
                            <div className={styles.cropArea}>
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                >
                                    <img
                                        ref={cropImageRef}
                                        src={(() => {
                                            const flash = flashes.find(f => f.id === cropFlashId);
                                            // 크롭 모달에서는 항상 원본 이미지 사용 (재크롭 시에도 원본에서 시작)
                                            return flash ? flash.image_url : '';
                                        })()}
                                        alt="크롭 이미지"
                                        style={{ maxWidth: '100%', maxHeight: '60vh' }}
                                    />
                                </ReactCrop>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* 하단 중앙 토스트 메시지 */}
            {saveMessage && (
                <div className={styles.toastMessage}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {saveMessage}
                </div>
            )}

            {/* 플렁슈 생성 날짜 */}
            {planche?.created_at && (
                <div className={styles.createdDate}>
                    Created: {new Date(planche.created_at).toLocaleDateString()}
                </div>
            )}
        </div >
    );
};

export default PlancheEditorPage;
